/**************
 New Cart Drawer — namespaced (#ncdCart / .ncd)
 Self-contained; does not touch the legacy #miniCart drawer.
 ***************/
(function () {
  'use strict';

  const dialog = document.getElementById('ncdCart');
  if (!dialog) return;

  const THRESHOLD = parseInt(dialog.dataset.freeShippingThreshold, 10) || 0;
  const MSG_UNLOCKED = dialog.dataset.msgUnlocked || "You've Unlocked Free Shipping!";
  const MSG_REMAINING = dialog.dataset.msgRemaining || "Spend [amount] to unlock Free Shipping";
  const freeGifts = window.ncdFreeGifts || [];
  const hiddenSKUs = window.ncdHiddenSKUs || [];
  /* If non-empty, free shipping is unlocked by having one of these products in the
     cart instead of by hitting the spend threshold. Empty = default milestone mode. */
  const freeShipProducts = window.ncdFreeShipProducts || [];
  /* Extra unlock rules (OR'd with the above): any subscription longer than 1 month,
     and — for the selected products — only count them when they're subscribed. */
  const FREE_SHIP_MULTIMONTH = dialog.dataset.freeShipMultimonth === 'true';
  const FREE_SHIP_SELECTED_REQ_SUB = dialog.dataset.freeShipSelectedSub === 'true';

  /* Subscribe & Save upsell box (copy from section settings) */
  const subBoxCfg = window.ncdSubBox || {};
  const subBoxPerks = String(subBoxCfg.perksRaw || '').split('\n').map(s => s.trim()).filter(Boolean);
  const productCache = {};

  let cart = null;
  let isUpdating = false;
  let detailsOpen = false;
  let renderToken = 0;

  const els = {
    count: document.getElementById('ncdCount'),
    progress: document.getElementById('ncdProgress'),
    progressText: document.getElementById('ncdProgressText'),
    progressFill: document.getElementById('ncdProgressFill'),
    items: document.getElementById('ncdItems'),
    gifts: document.getElementById('ncdGifts'),
    empty: document.getElementById('ncdEmpty'),
    footer: document.getElementById('ncdFooter'),
    total: document.getElementById('ncdTotal'),
    subtotal: document.getElementById('ncdSubtotal'),
    savings: document.getElementById('ncdSavings'),
    itemsCount: document.getElementById('ncdItemsCount'),
    toggleDetails: document.getElementById('ncdToggleDetails'),
    checkout: dialog.querySelector('.ncd__checkout'),
    recs: document.getElementById('ncdRecs'),
    recsTrack: document.getElementById('ncdRecsTrack'),
  };

  const hasGiftsConfigured = els.gifts && els.gifts.querySelectorAll('.ncd__gift').length > 0;
  const hasRecs = els.recs && els.recs.querySelectorAll('.ncd__rec').length > 0;

  function money(cents) { return '$' + (cents / 100).toFixed(2); }

  function cartSig(c) {
    if (!c || !c.items) return '0';
    return c.item_count + '|' + c.items_subtotal_price + '|' + c.items.map(function (i) {
      return i.key + ':' + i.quantity + ':' + ((i.selling_plan_allocation && i.selling_plan_allocation.selling_plan && i.selling_plan_allocation.selling_plan.id) || '');
    }).join(',');
  }

  function billingText(name) {
    if (!name) return '';
    let n = name.replace(/^Deliver(y)?\s+/i, '').trim();
    if (!/^every/i.test(n)) n = 'every ' + n;
    return 'Billed ' + n.toLowerCase();
  }

  /* ── Open / Close ─────────────────────────────────── */
  async function openCart(skipFetch) {
    if (isUpdating) return;
    isUpdating = true;
    try {
      // Open instantly using the cart we already have; only re-render if it actually changed.
      const hadCart = !!cart;
      if (hadCart) render(cart);
      if (!dialog.open) dialog.showModal();
      document.body.classList.add('ncd-no-scroll');

      if (!skipFetch || !cart) {
        const fresh = await fetch('/cart.js').then(r => r.json());
        const changed = !hadCart || cartSig(fresh) !== cartSig(cart);
        cart = fresh;
        if (changed) render(cart);
      }

      const updated = await handleFreeGifts(cart);
      if (updated) { cart = updated; render(cart); }
    } catch (e) {
      console.error('New cart drawer error:', e);
    } finally {
      isUpdating = false;
    }
  }

  function closeCart() {
    if (dialog.open) dialog.close();
    document.body.classList.remove('ncd-no-scroll');
  }

  /* ── Counts ───────────────────────────────────────── */
  function effectiveCount(c) {
    if (!c || !c.items) return 0;
    return c.items.reduce((n, it) => {
      if (freeGifts.includes(it.variant_id)) return n;
      if (hiddenSKUs.includes(it.product_id)) return n;
      return n + it.quantity;
    }, 0);
  }

  /* ── Render ───────────────────────────────────────── */
  function render(c) {
    renderToken++;
    const count = effectiveCount(c);
    document.querySelectorAll('.headerCart').forEach(el => el.setAttribute('count', count));
    if (els.count) els.count.textContent = count;

    els.items.innerHTML = '';

    if (count === 0) {
      setEmptyState();
      return;
    }
    setActiveState();

    c.items.forEach(item => {
      if (hiddenSKUs.includes(item.product_id)) return;
      if (freeGifts.includes(item.variant_id)) return;

      const planName = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
        ? item.selling_plan_allocation.selling_plan.name : null;
      const oUnit = originalUnit(item, planName);
      const compareUnit = oUnit > (item.final_line_price / item.quantity) ? oUnit : 0;

      els.items.insertAdjacentHTML('beforeend', buildItem(item, planName, compareUnit));
    });

    updateTotals(c);
    attachItemListeners();
    injectSubBoxes(c, renderToken);
  }

  /* The "main"/original unit price for a line — used for the crossed-out price and the
     Subtotal. Priority: the variant's compare-at price (if the cart exposes it and it's
     higher) → the subscription's regular (compare-at) unit → the pre-discount unit → the
     plain final unit when there's no discount at all. Always >= the final unit price. */
  function originalUnit(item, planName) {
    const finalUnit = item.final_line_price / item.quantity;
    if (item.compare_at_price && item.compare_at_price > finalUnit) return item.compare_at_price;
    if (planName && item.selling_plan_allocation && item.selling_plan_allocation.compare_at_price) {
      return item.selling_plan_allocation.compare_at_price;
    }
    if (item.original_line_price > item.final_line_price) return item.original_line_price / item.quantity;
    return finalUnit;
  }

  /* Cart-level numbers (subtotal/savings/total/count/progress/gifts). Reused for the
     partial refresh after subscribing so the item list (and the upsell box) aren't rebuilt. */
  function updateTotals(c) {
    const count = effectiveCount(c);
    document.querySelectorAll('.headerCart').forEach(el => el.setAttribute('count', count));
    if (els.count) els.count.textContent = count;
    if (els.itemsCount) els.itemsCount.textContent = count;

    let originalTotal = 0;   // "main" total — compare-at / regular price of every line
    let finalTotal = 0;      // what the customer actually pays
    c.items.forEach(item => {
      if (hiddenSKUs.includes(item.product_id)) return;
      if (freeGifts.includes(item.variant_id)) return;
      const planName = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
        ? item.selling_plan_allocation.selling_plan.name : null;
      finalTotal += item.final_line_price;
      originalTotal += originalUnit(item, planName) * item.quantity;
    });
    const savings = originalTotal - finalTotal;

    if (els.total) els.total.textContent = money(finalTotal);          // Total    = final price paid
    if (els.subtotal) {                                                // Subtotal = main/compare-at total
      els.subtotal.textContent = money(originalTotal);
      els.subtotal.classList.toggle('is-struck', savings > 0);         // strike through when Total < Subtotal
    }
    if (els.savings) {                                                 // Savings  = Subtotal − Total
      els.savings.textContent = money(Math.max(0, savings));
      const savingsRow = els.savings.closest('.ncd__detail-row');
      if (savingsRow) savingsRow.style.display = savings > 0 ? '' : 'none';
    }
    updateProgress(c);

    const hasSub = c.items.some(it => it.selling_plan_allocation && it.selling_plan_allocation.selling_plan && it.selling_plan_allocation.selling_plan.name);
    if (els.gifts) els.gifts.classList.toggle('is-active', !!(hasSub && hasGiftsConfigured));
    updateRecs(c);
  }

  function buildItem(item, planName, compareUnit) {
    const unit = item.final_line_price / item.quantity;
    const compareHtml = compareUnit ? `<s class="ff-gtm n-f-14">${money(compareUnit)}</s>` : '';

    const parts = [];
    if (item.variant_title && item.variant_title !== 'Default Title') {
      parts.push(`<span class="ncd__item-variant ff-gtm n-f-14">${item.variant_title}</span>`);
    }
    if (planName) {
      parts.push(`<span class="ff-gtm n-f-13">${billingText(planName)}</span>`);
    }
    const subLine = parts.length
      ? `<div class="ncd__item-sub">${parts.join('<span class="ncd__item-dot"></span>')}</div>`
      : '';

    // Quantity stepper — one-time purchases only (subscriptions keep delete-only;
    // their quantity is controlled by the subscription plan).
    const qtyHtml = planName ? '' : `
        <div class="ncd__item-qty">
          <button type="button" class="ncd__qty-btn ncd__qty-minus" aria-label="Decrease quantity"${item.quantity <= 1 ? ' disabled' : ''}>
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 8h10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          </button>
          <span class="ncd__qty-val ff-gtmb n-f-16">${item.quantity}</span>
          <button type="button" class="ncd__qty-btn ncd__qty-plus" aria-label="Increase quantity">
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          </button>
        </div>`;

    return `
    <div class="ncd__item" data-key="${item.key}">
      <img class="ncd__item-img" src="${item.image}&width=200" alt="">
      <div class="ncd__item-info">
        <p class="ncd__item-title ff-gtmb n-f-18">${item.product_title}</p>
        ${subLine}
        <div class="ncd__item-bottom">
          <div class="ncd__item-price ff-gtmb n-f-14">${money(unit)} ${compareHtml}</div>
          ${qtyHtml}
        </div>
      </div>
      <button class="ncd__item-delete" type="button" aria-label="Remove ${item.product_title}">
        <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.375 3.125H15.625M3.125 6.25H21.875M19.7917 6.25L19.0612 17.2076C18.9516 18.8516 18.8968 19.6736 18.5417 20.2969C18.2291 20.8456 17.7575 21.2868 17.1892 21.5622C16.5437 21.875 15.7199 21.875 14.0722 21.875H10.9278C9.28011 21.875 8.45628 21.875 7.81077 21.5622C7.24246 21.2868 6.77093 20.8456 6.45832 20.2969C6.10324 19.6736 6.04844 18.8516 5.93884 17.2076L5.20833 6.25M10.4167 10.9375V16.1458M14.5833 10.9375V16.1458" stroke="currentColor" stroke-width="2.08333" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>`;
  }

  /* ── States ───────────────────────────────────────── */
  function setActiveState() {
    els.progress.style.display = 'flex';
    els.items.style.display = 'flex';
    els.footer.classList.add('is-active');
    els.empty.classList.remove('is-active');
  }

  function setEmptyState() {
    els.progress.style.display = 'none';
    els.items.style.display = 'none';
    els.footer.classList.remove('is-active');
    els.gifts.classList.remove('is-active');
    if (els.recs) els.recs.classList.remove('is-active');
    els.empty.classList.add('is-active');
  }

  function planNameOf(it) {
    return (it.selling_plan_allocation && it.selling_plan_allocation.selling_plan)
      ? it.selling_plan_allocation.selling_plan.name : null;
  }

  /* Is a subscription plan's interval longer than one month?
     "every 2 months" / "every year" → yes;  "every month" / weekly / one-time → no. */
  function subIsMultiMonth(planName) {
    if (!planName) return false;
    const s = planName.toLowerCase();
    if (/year/.test(s)) return true;
    const m = s.match(/(\d+)\s*month/);
    return m ? parseInt(m[1], 10) > 1 : false;
  }

  /* ── Progress bar ─────────────────────────────────── */
  /* Free shipping unlocks when ANY enabled rule passes:
       1. (optional) the cart has a subscription longer than 1 month;
       2. the cart has one of the picked "free shipping products" — and, if the
          "requires subscription" toggle is on, that product is subscribed (any plan);
       3. (default) the spend subtotal reaches the milestone threshold.
     Rule 3 is skipped whenever products are picked, matching the override behavior:
     in that case a locked cart hides the bar instead of showing a spend target. */
  function updateProgress(c) {
    if (!els.progressFill || !els.progress) return;
    const items = (c && c.items) || [];

    let unlocked = false;

    // Rule 1 — any subscription longer than 1 month.
    if (FREE_SHIP_MULTIMONTH) {
      unlocked = unlocked || items.some(function (it) { return subIsMultiMonth(planNameOf(it)); });
    }

    // Rule 2 — picked products (optionally only when they're subscribed).
    const productMode = freeShipProducts.length > 0;
    if (productMode) {
      unlocked = unlocked || items.some(function (it) {
        if (freeShipProducts.indexOf(it.product_id) === -1) return false;
        if (FREE_SHIP_SELECTED_REQ_SUB && !planNameOf(it)) return false;
        return true;
      });
    }

    // Rule 3 — spend milestone (default; ignored while products are picked).
    const milestoneMode = !productMode && THRESHOLD > 0;
    const shippingSubtotal = (c && c.items_subtotal_price) || 0;
    if (milestoneMode && shippingSubtotal >= THRESHOLD) unlocked = true;

    if (unlocked) {
      els.progress.style.display = 'flex';
      els.progressFill.style.width = '100%';
      els.progress.style.setProperty('--ncd-progress', '100%');
      els.progressText.innerHTML = MSG_UNLOCKED;
      els.progressFill.classList.add('is-full');
      els.progressFill.classList.remove('is-glow');
      return;
    }

    if (milestoneMode) {
      els.progress.style.display = 'flex';
      const pct = Math.min((shippingSubtotal / THRESHOLD) * 100, 100);
      const remaining = (THRESHOLD - shippingSubtotal) / 100;
      els.progressFill.style.width = pct + '%';
      els.progress.style.setProperty('--ncd-progress', pct + '%');
      els.progressText.innerHTML = MSG_REMAINING.replace('[amount]', `<b>$${remaining.toFixed(2)}</b>`);
      els.progressFill.classList.remove('is-full');
      els.progressFill.classList.toggle('is-glow', pct > 0);
      return;
    }

    // Products picked but nothing qualifies (or no milestone) → hide the bar.
    els.progress.style.display = 'none';
  }

  /* ── You May Also Like (recommendations) ──────────── */
  /* Shows the slider when the cart has items; hides any recommended product that's
     already in the cart. Cards are rendered once server-side — we only toggle visibility. */
  function updateRecs(c) {
    if (!els.recs || !hasRecs) return;
    if (effectiveCount(c) === 0) { els.recs.classList.remove('is-active'); return; }
    const inCart = {};
    (c.items || []).forEach(it => { inCart[it.product_id] = true; });
    let visible = 0;
    els.recs.querySelectorAll('.ncd__rec').forEach(card => {
      const pid = parseInt(card.getAttribute('data-rec-product'), 10);
      const hide = !!inCart[pid];
      card.hidden = hide;
      if (!hide) visible++;
    });
    els.recs.classList.toggle('is-active', visible > 0);
    requestAnimationFrame(updateRecArrows);
  }

  function scrollRecs(dir) {
    if (!els.recsTrack) return;
    const card = els.recsTrack.querySelector('.ncd__rec:not([hidden])');
    const step = card ? card.getBoundingClientRect().width + 15 : 220;
    els.recsTrack.scrollBy({ left: dir * step, behavior: 'smooth' });
  }

  /* Disable prev/next at the track ends (and when nothing overflows) so clicking
     past the first/last card is a clean no-op instead of scrolling into empty space. */
  function updateRecArrows() {
    if (!els.recsTrack || !els.recs) return;
    var t = els.recsTrack;
    var max = t.scrollWidth - t.clientWidth;
    var noScroll = max <= 1;
    var prev = els.recs.querySelector('.ncd__recs-prev');
    var next = els.recs.querySelector('.ncd__recs-next');
    if (prev) prev.disabled = noScroll || t.scrollLeft <= 1;
    if (next) next.disabled = noScroll || t.scrollLeft >= max - 1;
  }

  /* ADD from a recommendation — one-time line (no selling plan), then re-render. */
  async function addRec(variantId, btn) {
    if (isUpdating || !variantId) return;
    isUpdating = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
      });
      if (!res.ok) throw new Error('Cart add failed: ' + res.status);
    } catch (e) {
      console.error('NCD rec add error:', e);
      if (btn) btn.classList.remove('is-loading');
    } finally {
      isUpdating = false;
      await openCart();
    }
  }

  /* ── Free gifts (auto add/remove on subscription) ─── */
  async function handleFreeGifts(currentCart) {
    if (!freeGifts.length) return null;

    const cartHasSub = currentCart.items.some(it => it.selling_plan_allocation && it.selling_plan_allocation.selling_plan && it.selling_plan_allocation.selling_plan.name);
    const giftsInCart = currentCart.items.filter(it => freeGifts.includes(it.variant_id));
    const giftIdsInCart = giftsInCart.map(it => it.variant_id);

    let updates = {};
    let itemsToAdd = [];
    let needsUpdate = false;

    if (cartHasSub) {
      freeGifts.forEach(giftId => {
        if (!giftIdsInCart.includes(giftId)) {
          itemsToAdd.push({ id: giftId, quantity: (giftId === 50618185777466) ? 2 : 1 });
          needsUpdate = true;
        }
      });
    } else if (giftsInCart.length > 0) {
      giftsInCart.forEach(it => { updates[it.key] = 0; });
      needsUpdate = true;
    }

    if (!needsUpdate) return null;

    try {
      if (Object.keys(updates).length) {
        await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
      }
      if (itemsToAdd.length) {
        await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: itemsToAdd }) });
      }
      return await fetch('/cart.js').then(r => r.json());
    } catch (e) {
      console.error('NCD free gift error:', e);
      return null;
    }
  }

  /* ── Item listeners (delete) ──────────────────────── */
  function attachItemListeners() {
    els.items.querySelectorAll('.ncd__item').forEach(el => {
      const key = el.getAttribute('data-key');
      const del = el.querySelector('.ncd__item-delete');
      if (del) del.addEventListener('click', () => removeItem(key, el));

      const minus = el.querySelector('.ncd__qty-minus');
      const plus = el.querySelector('.ncd__qty-plus');
      const valEl = el.querySelector('.ncd__qty-val');
      if (minus && plus && valEl) {
        const cur = parseInt(valEl.textContent, 10) || 1;
        minus.addEventListener('click', () => { if (cur > 1) changeQty(key, cur - 1); });
        plus.addEventListener('click', () => changeQty(key, cur + 1));
      }
    });
  }

  async function changeQty(key, qty) {
    if (isUpdating || qty < 1) return;
    isUpdating = true;
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [key]: qty } })
      });
      if (!res.ok) throw new Error('Cart update failed: ' + res.status);
    } catch (e) {
      console.error('NCD qty error:', e);
    } finally {
      isUpdating = false;
      await openCart();
    }
  }

  async function removeItem(key, el) {
    if (isUpdating) return;
    el.classList.add('is-removing');
    isUpdating = true;
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [key]: 0 } })
      });
      if (!res.ok) throw new Error('Cart update failed: ' + res.status);
    } catch (e) {
      console.error('NCD remove error:', e);
      el.classList.remove('is-removing');
    } finally {
      isUpdating = false;
      await openCart();
    }
  }

  /* ── Subscribe & Save upsell (subscription upgrade ladder) ──── */
  /* Per-product variant data (ordered, with sub price + hide_on_pdp) from the
     product.upsell.liquid template. /products/<handle>.js can't return metafields. */
  function getUpsellData(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + handle + '?view=upsell')
      .then(r => r.text())
      .then(t => { const d = JSON.parse(t); productCache[handle] = d; return d; })
      .catch(() => null);
  }

  /* The next rung on the ladder for a cart line, or null when there's nothing to offer:
       one-time line         -> the SAME variant as a subscription (convert in place)
       subscription line     -> the next visible (non-hidden), available variant with a
                                plan (upgrade)
       top-tier subscription -> null (no box) */
  async function getUpsellTarget(item) {
    const data = await getUpsellData(item.handle);
    if (!data || !data.variants) return null;
    const variants = data.variants;
    const idx = variants.findIndex(v => v.id === item.variant_id);
    if (idx === -1) return null;
    const isSub = !!(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);

    let target = null;
    if (!isSub) {
      const cur = variants[idx];
      if (cur.sub_plan_id && cur.sub_price != null) target = cur;   // convert this variant to a sub
    } else {
      for (let i = idx + 1; i < variants.length; i++) {             // climb to the next visible tier
        const v = variants[i];
        if (v.hidden || !v.available) continue;
        if (!v.sub_plan_id || v.sub_price == null) continue;
        target = v;
        break;
      }
    }
    if (!target) return null;

    const qty = item.quantity || 1;
    const lineSub = target.sub_price * qty;
    const lineReg = target.price * qty;
    // Per-unit ("per bottle/puck") savings = the tier's regular vs sub price divided by the
    // number of units in the pack (quantity metafield). unit_label comes from the metaobject.
    const perPack = target.qty && target.qty > 0 ? target.qty : 1;
    const unitSavings = Math.max(0, (target.price - target.sub_price) / perPack);
    return {
      variantId: target.id,
      sellingPlanId: target.sub_plan_id,
      qty: qty,
      label: target.label,
      planName: target.plan_name || '',
      unitLabel: target.unit_label || '',
      unitSavings: unitSavings,
      sameLine: target.id === item.variant_id,   // convert in place vs add-then-remove
      lineSub: lineSub,
      lineSavings: Math.max(0, lineReg - lineSub),
      discountPct: lineReg > 0 ? Math.round((lineReg - lineSub) / lineReg * 100) : 0
    };
  }

  function fillTokens(tpl, alloc) {
    return String(tpl || '')
      .replace(/\[price\]/g, money(alloc.lineSub))
      .replace(/\[savings\]/g, money(alloc.lineSavings))
      .replace(/\[discount\]/g, alloc.discountPct + '%')
      .replace(/\[variant\]/g, alloc.label || '')
      .replace(/\[billing\]/g, alloc.planName ? billingText(alloc.planName) : '')
      .replace(/\[plan\]/g, alloc.planName || '')
      .replace(/\[unit_savings\]/g, money(alloc.unitSavings || 0))
      .replace(/\[unit\]/g, alloc.unitLabel || '');
  }

  function buildSubBox(alloc) {
    const check = '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const perks = subBoxPerks.map(p => `<li class="ncd__subbox-perk ff-gtm n-f-13">${check}<span>${p}</span></li>`).join('');
    // Two copy sets: "subscribe" (converting a one-time line -> alloc.sameLine) and
    // "upgrade" (moving a subscription up to the next tier). Upgrade fields fall back to
    // the subscribe copy when left blank. [variant] resolves to the offered tier's name.
    const up = !alloc.sameLine;
    const tag      = (up && subBoxCfg.tagUp)      ? subBoxCfg.tagUp      : subBoxCfg.tag;
    const heading  = (up && subBoxCfg.headingUp)  ? subBoxCfg.headingUp  : subBoxCfg.heading;
    const priceTpl = (up && subBoxCfg.priceTplUp) ? subBoxCfg.priceTplUp : subBoxCfg.priceTpl;
    const discTpl  = (up && subBoxCfg.discountUp) ? subBoxCfg.discountUp : subBoxCfg.discountTpl;
    const button   = (up && subBoxCfg.buttonUp)   ? subBoxCfg.buttonUp   : subBoxCfg.buttonOffer;
    return `
      <div class="ncd__subbox${up ? ' ncd__subbox--upgrade' : ''}">
        ${tag ? `<span class="ncd__subbox-tag n-font-w-700 n-f-12">${fillTokens(tag, alloc)}</span>` : ''}
        <div class="ncd__subbox-body">
          ${heading ? `<p class="ncd__subbox-heading n-font-w-700 n-f-16">${fillTokens(heading, alloc)}</p>` : ''}
          <div class="ncd__subbox-pricerow">
            <span class="ncd__subbox-price ff-gtm n-f-13">${fillTokens(priceTpl, alloc)}</span>
            ${discTpl ? `<span class="ncd__subbox-disc n-f-13 ff-pxeb">${fillTokens(discTpl, alloc)}</span>` : ''}
          </div>
          ${perks ? `<ul class="ncd__subbox-perks">${perks}</ul>` : ''}
          <button type="button" class="ncd__subbox-btn ff-icm n-f-18">${fillTokens(button, alloc)}</button>
        </div>
      </div>`;
  }

  async function injectSubBoxes(c, token) {
    if (!subBoxCfg.enable) return;
    // Only the most recently added line gets an upsell box — not every line. Shopify lists
    // the newest item first, so the FIRST eligible line is the latest product.
    let item = null;
    for (const it of c.items) {
      if (hiddenSKUs.includes(it.product_id)) continue;
      if (freeGifts.includes(it.variant_id)) continue;
      item = it;
      break;
    }
    if (!item) return;
    // One-time line -> offer this variant as a sub; subscription line -> offer the next tier
    // up. getUpsellTarget returns null at the top of the ladder (then no box shows).
    const alloc = await getUpsellTarget(item);
    if (token !== renderToken) return;
    if (!alloc) return;
    const itemEl = els.items.querySelector('.ncd__item[data-key="' + item.key + '"]');
    if (!itemEl) return;
    if (itemEl.nextElementSibling && itemEl.nextElementSibling.classList.contains('ncd__subbox')) return;
    itemEl.insertAdjacentHTML('afterend', buildSubBox(alloc));
    const box = itemEl.nextElementSibling;
    const btn = box && box.querySelector('.ncd__subbox-btn');
    if (btn) btn.addEventListener('click', () => performUpsell(item, alloc, btn));
  }

  async function performUpsell(item, alloc, btn) {
    if (isUpdating) return;
    isUpdating = true;
    if (btn) btn.classList.add('is-loading');
    try {
      if (alloc.sameLine) {
        // one-time -> same variant subscription: convert the existing line in place.
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: alloc.qty, selling_plan: alloc.sellingPlanId })
        });
        if (!res.ok) throw new Error('Cart change failed: ' + res.status);
        cart = await res.json();
      } else {
        // Upgrade to the next variant on subscription: add the new line first, then remove
        // the old one. These two calls are NOT atomic — if the remove fails after the add
        // succeeds the shopper would be left with TWO subscriptions, so we roll the add back.
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: alloc.variantId, quantity: alloc.qty, selling_plan: alloc.sellingPlanId }] })
        });
        if (!addRes.ok) throw new Error('Cart add failed: ' + addRes.status);
        const added = await addRes.json();
        const addedKey = added && (added.key || (added.items && added.items[0] && added.items[0].key));
        const remRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        });
        if (!remRes.ok) {
          // Compensate: remove the line we just added so we never leave a duplicate sub.
          if (addedKey) {
            try {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: addedKey, quantity: 0 })
              });
            } catch (_) { /* reconciled in catch below */ }
          }
          throw new Error('Cart change failed: ' + remRes.status);
        }
        cart = await remRes.json();
      }
      // Subscribing/upgrading can unlock free gifts.
      const updated = await handleFreeGifts(cart);
      if (updated) cart = updated;
      // Full re-render so the line reflects the new subscription/tier (billing text +
      // discounted price, stepper removed) and the box re-renders for the next rung.
      render(cart);
    } catch (e) {
      console.error('NCD upsell error:', e);
      // Reconcile with the server so the drawer never shows a stale view that hides a
      // partial cart mutation (mirrors the openCart() re-sync the other mutators do).
      try {
        cart = await fetch('/cart.js').then(r => r.json());
        render(cart);
      } catch (_) { /* leave the current view; nothing better to show */ }
      if (btn) btn.classList.remove('is-loading');
    } finally {
      isUpdating = false;
    }
  }

  /* ── One-time listeners ───────────────────────────── */
  if (els.toggleDetails) {
    els.toggleDetails.addEventListener('click', () => {
      detailsOpen = !detailsOpen;
      els.toggleDetails.setAttribute('aria-expanded', detailsOpen ? 'true' : 'false');
      const label = els.toggleDetails.querySelector('.ncd__details-toggle-text');
      if (label) label.textContent = detailsOpen ? 'Hide Details' : 'Show Details';
    });
  }

  if (els.checkout) {
    els.checkout.addEventListener('click', () => els.checkout.classList.add('loading'));
  }

  /* Recommendations: ADD buttons + prev/next arrows (cards are static, so wire once) */
  if (els.recs) {
    els.recs.querySelectorAll('.ncd__rec-add').forEach(btn => {
      btn.addEventListener('click', () => { btn.classList.add('is-loading'); addRec(btn.getAttribute('data-rec-variant'), btn); });
    });
    const rprev = els.recs.querySelector('.ncd__recs-prev');
    const rnext = els.recs.querySelector('.ncd__recs-next');
    if (rprev) rprev.addEventListener('click', () => scrollRecs(-1));
    if (rnext) rnext.addEventListener('click', () => scrollRecs(1));
    if (els.recsTrack) els.recsTrack.addEventListener('scroll', updateRecArrows, { passive: true });
    window.addEventListener('resize', updateRecArrows);

    /* Collapse toggle (mobile only via CSS): chevron or a tap anywhere on the
       header — except the prev/next arrows — opens/closes the slider. */
    const rtoggle = els.recs.querySelector('.ncd__recs-toggle');
    const rhead = els.recs.querySelector('.ncd__recs-head');
    function toggleRecs() {
      const open = els.recs.classList.toggle('is-open');
      if (rtoggle) rtoggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) requestAnimationFrame(updateRecArrows);
    }
    if (rtoggle) rtoggle.addEventListener('click', e => { e.stopPropagation(); toggleRecs(); });
    if (rhead) rhead.addEventListener('click', e => {
      if (e.target.closest('.ncd__recs-nav') || e.target.closest('.ncd__recs-toggle')) return;
      toggleRecs();
    });
  }

  document.querySelectorAll('.openCart').forEach(el => el.addEventListener('click', () => openCart()));
  document.querySelectorAll('.closeCart').forEach(el => el.addEventListener('click', closeCart));
  dialog.addEventListener('click', e => { if (e.target === dialog) closeCart(); });
  dialog.addEventListener('close', () => document.body.classList.remove('ncd-no-scroll'));

  if (typeof dialogPolyfill !== 'undefined') dialogPolyfill.registerDialog(dialog);

  /* ── Header count on load ─────────────────────────── */
  document.addEventListener('DOMContentLoaded', async () => {
    try {
      const initial = await fetch('/cart.js').then(r => r.json());
      cart = initial;
      const count = effectiveCount(initial);
      document.querySelectorAll('.headerCart').forEach(el => el.setAttribute('count', count));
      if (els.count) els.count.textContent = count;
    } catch (e) {
      console.error('NCD header count error:', e);
    }
  });

  // Expose the global API the rest of the theme relies on (product pages call openCart()
  // after add-to-cart). Keeps the new drawer a drop-in replacement for the legacy globals.
  window.openCart = openCart;
  window.closeCart = closeCart;
  window.ncdOpenCart = openCart;
  window.ncdCloseCart = closeCart;
})();
